import { useText } from "@sveabank/cc-common";
import { useLocation } from "react-router";
import { useFullAccessUsers } from "../features/full-access-users/hooks";
import { useFetchPackageSignersQuery } from "../services/package-agreement";
import type { NavPage } from "../types/navigation";
import type { Step } from "../types/step";
import { stepIs } from "../utils/application";
import { useApplicationDetails } from "./application";
import { usePackage } from "./package";
import { useApplicationId } from "./useApplicationId";
import { useSteps } from "./useSteps";

export const usePages = () => {
	const applicationId = useApplicationId();
	const { pathname } = useLocation();
	const {
		isOrgEf,
		representatives,
		isLoading: applicationDetailsIsLoading,
		applicant,
	} = useApplicationDetails();

	const { canManageFullAccessUsers } = useFullAccessUsers();

	const {
		hasCreditProduct,
		hasCheckCreditProduct,
		selectedPackageDataIsLoading,
		availablePackagesIsLoading,
	} = usePackage();
	const {
		areStepsCompleted,
		someStepExists,
		areStepsCompletedIfExists,
		steps,
		isLoading: stepsIsLoading,
	} = useSteps();
	const [t, texts] = useText();

	const { isLoading: packageSignersIsLoading } = useFetchPackageSignersQuery(applicationId);

	const packageSignStatuses = steps?.find(stepIs("SignPackageAgreementStep"))?.state.signers || [];
	const guarantorsSignStatuses =
		steps?.find(stepIs("SignGuarantorAgreementStep"))?.state.signers || [];

	const isSignCompleted = () => {
		if (isOrgEf || !hasCreditProduct) {
			return !!packageSignStatuses.find((pss) => pss.isSigned);
		}

		const applicantIsGuarantor = guarantorsSignStatuses.find(
			(gss) => gss.nationalId === applicant?.nationalId,
		);

		return (
			!!packageSignStatuses.find((pss) => pss.isSigned) &&
			(applicantIsGuarantor ? applicantIsGuarantor.isSigned : true)
		);
	};

	const kycHasWarning =
		steps?.find(stepIs("ConfirmBeneficialOwnershipStep"))?.state.status === "Declined";

	const isKycCompleted = () => {
		const confirmBoStep = steps?.find(stepIs("ConfirmBeneficialOwnershipStep"));

		const shouldRequireBeneficialOwner = !!(
			confirmBoStep?.state.beneficialOwnershipStatus !== "unspecifiedOrMissingInformation"
		);

		const requiredSteps: Array<Step["name"]> = [
			"AssignSniCodesStep",
			"ApplicationQuestionsStep",
			"AmlQuestionsStep",
			"BeneficialOwnerQuestionsStepV1",
		];

		if (shouldRequireBeneficialOwner) requiredSteps.push("ConfirmBeneficialOwnershipStep");

		return areStepsCompleted(...requiredSteps.filter((stepName) => steps?.find(stepIs(stepName))));
	};

	const isSettingsCompleted = () =>
		areStepsCompletedIfExists("ConfigureMastercardCreditStep", "ConfigureMastercardDirectStep");

	const isChoosePackageCompleted = hasCheckCreditProduct
		? areStepsCompleted("SelectPackageStep", "ConfigureBankGiroProductStep")
		: areStepsCompleted("SelectPackageStep");

	const initialArray = [
		{
			label: t(texts.navbar.choosePackage.label),
			url: `/choose-package/${applicationId}`,
			isCompleted: isChoosePackageCompleted,
			isAvailable: true,
			locked: isSignCompleted(),
		},
		{
			label: t(texts.navbar.credit.label),
			url: `/adjust-credit/${applicationId}`,
			isCompleted: areStepsCompleted("ConfigureCreditProductStep"),
			isAvailable: areStepsCompleted("SelectPackageStep", "ConfigureBankGiroProductStep"),
			hidden: !hasCreditProduct,
			locked: isSignCompleted(),
		},
		{
			label: t(texts.navbar.contact.label),
			url: `/contact/${applicationId}`,
			isCompleted: areStepsCompleted(
				"OrganizationContactInformationStep",
				"ApplicantContactInformationStep",
			),
			isAvailable: hasCreditProduct
				? areStepsCompleted("ConfigureCreditProductStep")
				: areStepsCompleted("SelectPackageStep", "ConfigureBankGiroProductStep"),
			locked: isSignCompleted(),
		},
		{
			label: t(texts.navbar.users.label),
			url: `/full-access-users/${applicationId}`,
			isCompleted: someStepExists("SignPackageAgreementStep"),
			isAvailable: areStepsCompleted(
				"OrganizationContactInformationStep",
				"ApplicantContactInformationStep",
			),
			hidden: !canManageFullAccessUsers,
			locked: isSignCompleted(),
		},
		{
			label: t(texts.signPage.heading),
			url: `/sign/${applicationId}`,
			isCompleted: isSignCompleted(),
			isAvailable:
				representatives?.length === 1
					? areStepsCompleted(
							"OrganizationContactInformationStep",
							"ApplicantContactInformationStep",
						)
					: someStepExists("SignPackageAgreementStep"),
			locked: isSignCompleted(),
		},
		{
			label: t(texts.navbar.settings.label),
			url: `/settings/${applicationId}`,
			isCompleted: isSettingsCompleted(),
			isAvailable: isSignCompleted(),
			hidden: !someStepExists("ConfigureMastercardCreditStep", "ConfigureMastercardDirectStep"),
		},
		{
			label: t(texts.navbar.kyc.label),
			url: `/kyc/${applicationId}`,
			isCompleted: isKycCompleted(),
			isAvailable: someStepExists("ConfigureMastercardCreditStep", "ConfigureMastercardDirectStep")
				? isSettingsCompleted()
				: isSignCompleted(),
			hasWarning: kycHasWarning,
		},
		{
			label: t(texts.navbar.summary.label),
			url: `/summary/${applicationId}`,
			isCompleted: false,
			isAvailable: isKycCompleted(),
		},
	].filter((page) => !page.hidden);

	const pages: Array<NavPage> = initialArray.map((page, index) => ({
		...page,
		totalSteps: initialArray.length,
		stepNumber: index + 1,
	}));

	const resumeToPage = pages.find((p) => !p.isCompleted)?.url;
	const currentIndex = pages.map((a) => a.url).indexOf(pathname);

	const nextPageAvailable = !!pages[currentIndex + 1]?.isAvailable;

	return {
		pages,
		resumeToPage,
		nextPageAvailable,
		isLoading:
			selectedPackageDataIsLoading ||
			availablePackagesIsLoading ||
			applicationDetailsIsLoading ||
			packageSignersIsLoading ||
			stepsIsLoading,
		nextPage: pages[currentIndex + 1]?.url,
		prevPage: pages[currentIndex > 0 ? currentIndex - 1 : 0]?.url,
	};
};
